import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_LIST } from '../../../../../constants/common'
import { FORMIK_EMPTY_VALUE } from '../../../../../constants/validation'
import {
  BUILDING_OPTIONS_WITH_AVATAR_PARAMS,
  BuildingMultiSelect,
  BuildingSelectFormikField,
} from '../../../../Building'
import {
  BuildingGroupMultiSelect,
  BuildingGroupSelectFormikField,
} from '../../../../BuildingGroup'
import { FieldStackedLabel } from '../../../../Field'
import { FlatMultiSelect, FlatSelectFormikField } from '../../../../Flat'
import { FormFieldsContainer, FormFieldSelectNG } from '../../../../Form'
import { FormikField } from '../../../../Formik'
import { ButtonSelect } from '../../../../Select'
import { UserInfoStatusMultiSelect } from '../../../../User'
import { getFlats } from '../../EmailRecipientsField.api'
import {
  EMAIL_RECIPIENTS_BUILDING_FIELD_NAME,
  EMAIL_RECIPIENTS_BUILDING_GROUP_FIELD_NAME,
  EMAIL_RECIPIENTS_FLAT_FIELD_NAME,
  EMAIL_RECIPIENTS_GROUP_FIELD_NAME,
  EMAIL_RECIPIENTS_ROLE_FIELD_NAME,
  EMAIL_RECIPIENTS_USER_INFO_STATUS_FIELD_NAME,
  GROUP_OPTIONS,
} from '../EmailRecipientsFilterModal.constants'
import { getOptions } from '../EmailRecipientsFilterModal.utils'

import {
  filterFlatOptions,
  getRoleOptions,
  hasEmailRecipientsBuildingScope,
  isEmailRecipientsPropertyOwnerRole,
  toFlatWithBuildingSelectItems,
} from './FormFields.utils'

function FormFields() {
  const { t } = useTranslation()

  const { setFieldValue, values } = useFormikContext()

  useEffect(() => {
    if (values[EMAIL_RECIPIENTS_BUILDING_FIELD_NAME].length === 0) {
      setFieldValue(EMAIL_RECIPIENTS_FLAT_FIELD_NAME, EMPTY_LIST)
    } else {
      setFieldValue(EMAIL_RECIPIENTS_FLAT_FIELD_NAME, filterFlatOptions(values))
    }
  }, [values[EMAIL_RECIPIENTS_BUILDING_FIELD_NAME]])

  useEffect(() => {
    if (
      !hasEmailRecipientsBuildingScope(values) &&
      isEmailRecipientsPropertyOwnerRole(
        values[EMAIL_RECIPIENTS_ROLE_FIELD_NAME]
      )
    ) {
      setFieldValue(EMAIL_RECIPIENTS_ROLE_FIELD_NAME, FORMIK_EMPTY_VALUE)
    }
  }, [
    values[EMAIL_RECIPIENTS_BUILDING_FIELD_NAME],
    values[EMAIL_RECIPIENTS_BUILDING_GROUP_FIELD_NAME],
  ])

  const flatApiParams = {
    building: values[EMAIL_RECIPIENTS_BUILDING_FIELD_NAME].map(i => {
      return ['building', i.value]
    }),
  }

  const disabledFlatField =
    values[EMAIL_RECIPIENTS_BUILDING_FIELD_NAME].length === 0

  return (
    <FormFieldsContainer>
      <FormikField
        disabled
        Component={FormFieldSelectNG}
        Field={FieldStackedLabel}
        Select={ButtonSelect}
        controlProps={{
          contentProps: {
            placeholderProps: {
              text: t('Email.RecipientsGroupFilterPlaceholderKey'),
            },
          },
        }}
        fieldComponentProps={{
          disabled: true,
          label: t('Email.RecipientsGroupFilterLabelKey'),
        }}
        name={EMAIL_RECIPIENTS_GROUP_FIELD_NAME}
        options={getOptions(GROUP_OPTIONS, t)}
        tooltip={t('Email.FieldCurrentlyUnavailableTooltipKey')}
      />
      <FormikField
        Component={FormFieldSelectNG}
        Field={FieldStackedLabel}
        Select={ButtonSelect}
        controlProps={{
          contentProps: {
            placeholderProps: {
              text: t('Email.RecipientsRoleFilterPlaceholderKey'),
            },
          },
        }}
        fieldComponentProps={{
          label: t('Email.RecipientsRoleFilterLabelKey'),
        }}
        name={EMAIL_RECIPIENTS_ROLE_FIELD_NAME}
        options={getRoleOptions(values, t)}
      />
      <BuildingGroupSelectFormikField
        multi
        Field={FieldStackedLabel}
        Select={BuildingGroupMultiSelect}
        controlProps={{
          contentProps: {
            inputProps: {
              placeholder: t(
                'Email.RecipientsBuildingGroupFilterPlaceholderKey'
              ),
            },
          },
        }}
        fieldComponentProps={{
          label: t('Email.RecipientsBuildingGroupFilterLabelKey'),
        }}
        name={EMAIL_RECIPIENTS_BUILDING_GROUP_FIELD_NAME}
      />
      <BuildingSelectFormikField
        multi
        Field={FieldStackedLabel}
        Select={BuildingMultiSelect}
        apiParams={BUILDING_OPTIONS_WITH_AVATAR_PARAMS}
        controlProps={{
          contentProps: {
            inputProps: {
              placeholder: t('Email.RecipientsBuildingFilterPlaceholderKey'),
            },
          },
        }}
        fieldComponentProps={{
          label: t('Email.RecipientsBuildingFilterLabelKey'),
        }}
        name={EMAIL_RECIPIENTS_BUILDING_FIELD_NAME}
      />
      <FlatSelectFormikField
        multi
        Field={FieldStackedLabel}
        Select={FlatMultiSelect}
        apiParams={flatApiParams}
        controlProps={{
          contentProps: {
            inputProps: {
              placeholder: t('Email.RecipientsFlatFilterPlaceholderKey'),
            },
          },
        }}
        disabled={disabledFlatField}
        fieldComponentProps={{
          label: t('Email.RecipientsFlatFilterLabelKey'),
        }}
        getData={getFlats}
        name={EMAIL_RECIPIENTS_FLAT_FIELD_NAME}
        tooltip={
          disabledFlatField ? t('Email.SelectBuildingValueTooltipKey') : null
        }
        toPreparedItems={toFlatWithBuildingSelectItems}
      />
      <FormikField
        multi
        Component={FormFieldSelectNG}
        Field={FieldStackedLabel}
        Select={UserInfoStatusMultiSelect}
        controlProps={{
          contentProps: {
            inputProps: {
              placeholder: t(
                'Email.RecipientsUserInfoStatusFilterPlaceholderKey'
              ),
            },
          },
        }}
        emptyValue={EMPTY_LIST}
        fieldComponentProps={{
          label: t('Email.RecipientsUserInfoStatusFilterLabelKey'),
        }}
        name={EMAIL_RECIPIENTS_USER_INFO_STATUS_FIELD_NAME}
      />
    </FormFieldsContainer>
  )
}

export default FormFields
